import React, { type PropsWithChildren, type FC } from 'react';
import { Route, useParamsLegacy as useParams } from '@leagueplatform/routing';
import { MasonryEngine } from '@leagueplatform/masonry-engine';
import { StackLayout } from '@leagueplatform/genesis-core';
import { useSearchParams } from '@leagueplatform/web-common';
import { getMasonryWSRootNode } from '@leagueplatform/masonry-api';
import { logError } from '#utils/log-error.ts';
import { wsDriver } from '../../masonry-engine/masonry-WS-driver/driver';
import { Masonry } from '../masonry.component';

type GenericMasonryPageProps = {
  pageWrapperComponent?: FC;
};

type GenericMasonryPageRouteProps = GenericMasonryPageProps & {
  baseName: string;
};

const DefaultPageWrapperComponent = ({ children }: PropsWithChildren<{}>) => (
  <StackLayout
    horizontalAlignment="stretch"
    css={{ padding: '$two', maxWidth: '800px' }}
  >
    {children}
  </StackLayout>
);

export const GenericMasonryPage = ({
  pageWrapperComponent = DefaultPageWrapperComponent,
}: GenericMasonryPageProps) => {
  const { version: versionParam, id: idParam } =
    useParams<Record<'version' | 'id', string>>();
  const searchParam = useSearchParams();

  const versionSearchParam = searchParam.get('version');
  const idSearchParam = searchParam.get('nodeId');

  const id = idParam || idSearchParam;
  const version = versionParam || versionSearchParam;

  if (!id) {
    logError(`'id' URL param not found.`);
    return null;
  }

  const searchParams = new URLSearchParams(
    new URL(window.location?.href).search,
  );
  const screenName = searchParams.get('screenName') ?? '';
  const productArea = searchParams.get('productArea') ?? '';

  if (version === '1.5') {
    return React.createElement(
      pageWrapperComponent,
      null,
      <MasonryEngine
        driver={wsDriver}
        rootNode={getMasonryWSRootNode({
          appId: id,
          screenName,
          productArea,
        })}
      />,
    );
  }

  if (version === '2.0' || version === '2') {
    return React.createElement(
      pageWrapperComponent,
      null,
      <Masonry rootNodeId={id} initialQueryParams={{ test: 'test_param' }} />,
    );
  }

  logError(`version ${version} not supported for generic Masonry page.`);
  return null;
};

export const GenericMasonryPageRoute = ({
  baseName,
  pageWrapperComponent,
}: GenericMasonryPageRouteProps) => (
  <Route
    path={`${baseName}/:version/:id`}
    element={
      <>
        (
        <GenericMasonryPage pageWrapperComponent={pageWrapperComponent} />)
      </>
    }
  />
);
